import React, { useState, useEffect, useCallback } from "react";
import { Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";

const ROWS = 13;

// Agent IDs to hide from all views (add more as needed)
const HIDDEN_AGENT_IDS = ["022"];

/* ─── Shared styles ──────────────────────────────────────────────── */
const card = {
  background:   "var(--vz-secondary-bg, #fff)",
  border:       "1px solid var(--vz-border-color)",
  borderRadius: 10,
  boxShadow:    "0 1px 4px rgba(0,0,0,0.06)",
  overflow:     "hidden",
};

const statusMeta = (status) => {
  const s = (status || "").toLowerCase();
  if (s.includes("active") || s.includes("online"))
    return { text: "Online",          color: "var(--vz-success)", bg: "rgba(var(--vz-success-rgb),.12)", border: "rgba(var(--vz-success-rgb),.3)", dot: "#22c55e" };
  if (s.includes("disconnect") || s.includes("offline"))
    return { text: "Offline",         color: "var(--vz-danger)",  bg: "rgba(var(--vz-danger-rgb),.12)",  border: "rgba(var(--vz-danger-rgb),.3)",  dot: "#ef4444" };
  if (s.includes("never"))
    return { text: "Never Connected", color: "var(--vz-warning)", bg: "rgba(var(--vz-warning-rgb),.12)", border: "rgba(var(--vz-warning-rgb),.3)", dot: "#f59e0b" };
  return { text: status || "Unknown", color: "var(--vz-secondary-color)", bg: "rgba(var(--vz-secondary-rgb),.12)", border: "var(--vz-border-color)", dot: "#94a3b8" };
};

const fmtAgo = (ts) => {
  if (!ts) return "Never";
  const diff = Math.floor((Date.now() - new Date(ts)) / 60000);
  if (diff < 1)     return "Just now";
  if (diff < 60)    return `${diff}m ago`;
  if (diff < 1440)  return `${Math.floor(diff / 60)}h ago`;
  if (diff < 10080) return `${Math.floor(diff / 1440)}d ago`;
  return new Date(ts).toLocaleDateString();
};

const fmtTs = (ts) => {
  if (!ts) return "—";
  return new Date(ts).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

/* ─── Info row ───────────────────────────────────────────────────── */
const InfoRow = ({ label, value, mono, last, loading }) => (
  <div style={{
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "8px 0",
    borderBottom: last ? "none" : "1px solid var(--vz-border-color)"
  }}>
    <span style={{ fontSize: 12, color: "var(--vz-secondary-color)" }}>{label}</span>
    {loading ? (
      <div style={{ width: 80, height: 12, borderRadius: 6, background: "linear-gradient(90deg, var(--vz-border-color) 25%, var(--vz-light) 50%, var(--vz-border-color) 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }} />
    ) : (
      <span style={{ fontSize: 12, fontWeight: 600, color: "var(--vz-body-color)", fontFamily: mono ? "monospace" : "inherit" }}>
        {value || "—"}
      </span>
    )}
  </div>
);

/* ─── Agent Detail Side Panel ────────────────────────────────────── */
const AgentDetailPanel = ({ agent, onClose, authFetch }) => {
  const [events,         setEvents]         = useState([]);
  const [evLoading,      setEvLoading]      = useState(true);
  const [activeTab,      setActiveTab]      = useState("network");
  const [geo,            setGeo]            = useState(null);
  const [geoLoading,     setGeoLoading]     = useState(false);

  // ── Login History state ──
  const [loginHistory,   setLoginHistory]   = useState([]);
  const [loginLoading,   setLoginLoading]   = useState(false);

  const displayIp = agent.public_ip || (agent.ip !== "any" ? agent.ip : null);

  useEffect(() => {
    if (!agent) return;

    // Reset all state when agent changes
    setEvents([]);
    setLoginHistory([]);
    setGeo(null);

    // Fetch geo
    if (agent.public_ip) {
      setGeoLoading(true);
      authFetch(`/api/geo/ip/${encodeURIComponent(agent.public_ip)}`)
        .then(d => setGeo(d?.geo || null))
        .catch(() => setGeo(null))
        .finally(() => setGeoLoading(false));
    }

    // Fetch status history — machine events only (online/offline)
    setEvLoading(true);
    authFetch(`/api/agents/${encodeURIComponent(agent.name || agent.id)}/status-log?limit=20&days=30`)
      .then(d => {
        const all = d?.events || [];
        const machineOnly = all.filter(e => {
          const t = (e.event_type || "").toLowerCase();
          const cat = (e.event_category || "").toLowerCase();
          return cat === "machine_status" || t === "online" || t === "offline" || t === "status_change";
        });
        setEvents(machineOnly);
      })
      .catch(() => setEvents([]))
      .finally(() => setEvLoading(false));

    // Fetch login history (all events for Login History tab)
    setLoginLoading(true);
    authFetch(`/api/agents/${encodeURIComponent(agent.name || agent.id)}/status-log?limit=100&days=30`)
      .then(d => setLoginHistory(d?.events || []))
      .catch(() => setLoginHistory([]))
      .finally(() => setLoginLoading(false));

  }, [agent?.id, agent?.name]); // eslint-disable-line

  if (!agent) return null;

  const sm      = statusMeta(agent.status);
  const isOnline = sm.text === "Online";

  // ── Split login history by category ──
  const userEvents = loginHistory.filter(e => e.event_category === "user_session" || ["user_login","user_logout","session_change"].includes((e.event_type || "").toLowerCase()));

  const tabs = [
    { id: "network", label: "Network Info",   icon: "wifi"          },
    { id: "geo",     label: "Geo Location",   icon: "map-pin"       },
    { id: "history", label: "Status History", icon: "activity"      },
    { id: "login",   label: "Login History",  icon: "log-in"        },
  ];

  /* ── Login event helpers ── */
  const loginEventMeta = (ev) => {
    const t = (ev.event_type || "").toLowerCase();
    if (t === "online")         return { label: "Came Online",    icon: "arrow-up-circle",   color: "var(--vz-success)", bg: "rgba(var(--vz-success-rgb),.12)", border: "rgba(var(--vz-success-rgb),.3)" };
    if (t === "offline")        return { label: "Went Offline",   icon: "arrow-down-circle", color: "var(--vz-danger)",  bg: "rgba(var(--vz-danger-rgb),.12)",  border: "rgba(var(--vz-danger-rgb),.3)"  };
    if (t === "user_login")     return { label: "User Logged In",  icon: "log-in",           color: "#0284c7",           bg: "rgba(2,132,199,.1)",              border: "rgba(2,132,199,.25)"            };
    if (t === "user_logout")    return { label: "User Logged Out", icon: "log-out",          color: "#7c3aed",           bg: "rgba(124,58,237,.1)",             border: "rgba(124,58,237,.25)"           };
    if (t === "session_change" || t === "status_change") return { label: "Status Change", icon: "refresh-cw", color: "var(--vz-warning)", bg: "rgba(var(--vz-warning-rgb),.12)", border: "rgba(var(--vz-warning-rgb),.3)" };
    return { label: "Status Change", icon: "refresh-cw", color: "var(--vz-warning)", bg: "rgba(var(--vz-warning-rgb),.12)", border: "rgba(var(--vz-warning-rgb),.3)" };
  };

  // eslint-disable-next-line
  const isUserEvent = (ev) => {
    const t = (ev.event_type || "").toLowerCase();
    return ev.event_category === "user_session" || ["user_login","user_logout","session_change"].includes(t);
  };



  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.35)", zIndex: 1040, backdropFilter: "blur(2px)" }} />

      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0, width: 440,
        zIndex: 1050, background: "var(--vz-secondary-bg, #fff)",
        borderLeft: "1px solid var(--vz-border-color)",
        boxShadow: "-8px 0 32px rgba(0,0,0,.15)",
        display: "flex", flexDirection: "column",
        animation: "slideIn .25s ease",
      }}>
        <style>{`
          @keyframes slideIn { from { transform: translateX(100%); opacity:0 } to { transform: translateX(0); opacity:1 } }
          @keyframes pulse   { 0%,100%{box-shadow:0 0 0 2px rgba(34,197,94,.3)} 50%{box-shadow:0 0 0 5px rgba(34,197,94,.1)} }
          @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        `}</style>

        {/* Header */}
        <div style={{ padding: "18px 20px", borderBottom: "1px solid var(--vz-border-color)", display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <div style={{ width: 40, height: 40, borderRadius: "50%", background: sm.bg, border: `1px solid ${sm.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <FeatherIcon icon="monitor" size={18} style={{ color: sm.color }} />
              </div>
              <div style={{ minWidth: 0 }}>
                <h6 style={{ fontWeight: 700, color: "var(--vz-body-color)", margin: 0, fontSize: 15, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {agent.name || "Unknown"}
                </h6>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3, flexWrap: "wrap" }}>
                  <code style={{ fontSize: 11, color: "var(--vz-primary)", background: "rgba(var(--vz-primary-rgb),.1)", padding: "1px 6px", borderRadius: 4 }}>
                    ID: {agent.id}
                  </code>
                  {displayIp && (
                    <code style={{ fontSize: 11, color: "#0284c7", background: "rgba(2,132,199,.1)", padding: "1px 6px", borderRadius: 4, border: "1px solid rgba(2,132,199,.2)" }}>
                      {displayIp}
                    </code>
                  )}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, padding: "3px 12px", borderRadius: 20, background: sm.bg, color: sm.color, border: `1px solid ${sm.border}` }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: sm.dot, ...(isOnline ? { animation: "pulse 2s infinite" } : {}) }} />
                {sm.text}
              </span>
              <span style={{ fontSize: 11, color: "var(--vz-secondary-color)" }}>Last seen {fmtAgo(agent.last_updated)}</span>
            </div>
          </div>
          <button onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--vz-secondary-color)", padding: 4, flexShrink: 0, marginTop: -2 }}
            onMouseEnter={e => e.currentTarget.style.color = "var(--vz-danger)"}
            onMouseLeave={e => e.currentTarget.style.color = "var(--vz-secondary-color)"}
          >
            <FeatherIcon icon="x" size={20} />
          </button>
        </div>

        {/* Quick stats */}
        <div style={{ padding: "12px 20px", borderBottom: "1px solid var(--vz-border-color)", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, flexShrink: 0, background: "rgba(var(--vz-primary-rgb),.02)" }}>
          {[
            { icon: "cpu",      label: "OS",      value: agent.os ? agent.os.replace("Microsoft ", "") : "Unknown" },
            { icon: "wifi",     label: "IP",       value: displayIp || "DHCP/NAT" },
            { icon: "activity", label: "Events",   value: loginLoading ? "..." : `${loginHistory.length} in 30d` },
          ].map((r, i) => (
            <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, textAlign: "center" }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(var(--vz-primary-rgb),.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <FeatherIcon icon={r.icon} size={13} style={{ color: "var(--vz-primary)" }} />
              </div>
              <p style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--vz-secondary-color)", margin: 0 }}>{r.label}</p>
              <p style={{ fontSize: 11, color: "var(--vz-body-color)", margin: 0, fontWeight: 600, lineHeight: 1.3, fontFamily: r.label === "IP" ? "monospace" : "inherit" }}>{r.value}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", borderBottom: "1px solid var(--vz-border-color)", flexShrink: 0, overflowX: "auto" }}>
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              flex: 1, padding: "11px 6px", border: "none", background: "none", cursor: "pointer",
              fontSize: 12, fontWeight: activeTab === tab.id ? 700 : 500,
              color: activeTab === tab.id ? "var(--vz-primary)" : "var(--vz-secondary-color)",
              borderBottom: activeTab === tab.id ? "2px solid var(--vz-primary)" : "2px solid transparent",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
              transition: "all .15s", whiteSpace: "nowrap", minWidth: 90,
            }}>
              <FeatherIcon icon={tab.icon} size={13} />{tab.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "18px 20px" }}>

          {/* ── NETWORK INFO ─────────────────────────── */}
          {activeTab === "network" && (
            <>
              {agent.ip === "any" && (
                <div style={{ background: "rgba(2,132,199,.08)", border: "1px solid rgba(2,132,199,.2)", borderRadius: 8, padding: "10px 14px", marginBottom: 16, display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <FeatherIcon icon="info" size={14} style={{ color: "#0284c7", flexShrink: 0, marginTop: 1 }} />
                  <p style={{ fontSize: 12, color: "var(--vz-body-color)", margin: 0, lineHeight: 1.5 }}>
                    This agent uses dynamic addressing (DHCP/NAT). The IP shown is the most recent address found in security alerts.
                  </p>
                </div>
              )}
              <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--vz-secondary-color)", marginBottom: 8 }}>Agent Details</p>
              <div style={{ marginBottom: 20 }}>
                <InfoRow label="Agent Name"  value={agent.name}                      />
                <InfoRow label="Agent ID"    value={agent.id}        mono            />
                <InfoRow label="Internal IP" value={displayIp || "—"} mono          />
                <InfoRow label="OS"          value={agent.os || "Unknown"}           />
                <InfoRow label="Status"      value={sm.text}                         />
                <InfoRow label="Last Seen"   value={fmtTs(agent.last_updated)} last  />
              </div>
              <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--vz-secondary-color)", marginBottom: 8 }}>Connection Summary (30 days)</p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
                {[
                  { label: "Total",   value: events.length,                                               color: "var(--vz-primary)" },
                  { label: "Online",  value: events.filter(e => e.event_type === "online").length,         color: "var(--vz-success)" },
                  { label: "Offline", value: events.filter(e => e.event_type === "offline").length,        color: "var(--vz-danger)"  },
                ].map((s, i) => (
                  <div key={i} style={{ background: "var(--vz-secondary-bg)", border: "1px solid var(--vz-border-color)", borderRadius: 8, padding: "12px", textAlign: "center" }}>
                    {evLoading ? (
                      <div style={{ width: 32, height: 28, borderRadius: 6, background: "linear-gradient(90deg, var(--vz-border-color) 25%, var(--vz-light) 50%, var(--vz-border-color) 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite", margin: "0 auto 4px" }} />
                    ) : (
                      <p style={{ fontSize: 22, fontWeight: 800, color: s.color, margin: "0 0 2px" }}>{s.value}</p>
                    )}
                    <p style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--vz-secondary-color)", margin: 0 }}>{s.label}</p>
                  </div>
                ))}
              </div>
              {!evLoading && events.length > 0 && (
                <div style={{ padding: "12px 14px", background: "rgba(var(--vz-primary-rgb),.04)", border: "1px solid rgba(var(--vz-primary-rgb),.15)", borderRadius: 8 }}>
                  <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--vz-secondary-color)", margin: "0 0 8px" }}>Last Event</p>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 20,
                      background: events[0].event_type === "online" ? "rgba(var(--vz-success-rgb),.12)" : "rgba(var(--vz-danger-rgb),.12)",
                      color:      events[0].event_type === "online" ? "var(--vz-success)" : "var(--vz-danger)",
                      border:    `1px solid ${events[0].event_type === "online" ? "rgba(var(--vz-success-rgb),.3)" : "rgba(var(--vz-danger-rgb),.3)"}`,
                    }}>
                      <FeatherIcon icon={events[0].event_type === "online" ? "arrow-up" : "arrow-down"} size={11} />
                      {events[0].event_type === "online" ? "Came Online" : "Went Offline"}
                    </span>
                    <span style={{ fontSize: 12, color: "var(--vz-secondary-color)" }}>
                      {fmtTs(events[0].timestamp)} · {fmtAgo(events[0].timestamp)}
                    </span>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── GEO LOCATION ─────────────────────────── */}
          {activeTab === "geo" && (
            geoLoading ? (
              <div className="text-center py-5">
                <Spinner size="sm" color="primary" />
                <p style={{ marginTop: 8, fontSize: 13, color: "var(--vz-secondary-color)" }}>Looking up location...</p>
              </div>
            ) : !geo ? (
              <div className="text-center py-5" style={{ color: "var(--vz-secondary-color)" }}>
                <FeatherIcon icon="map-pin" size={36} className="d-block mx-auto mb-3" style={{ opacity: .3 }} />
                <p style={{ fontSize: 14, fontWeight: 600, margin: "0 0 4px" }}>No Location Data</p>
                <p style={{ fontSize: 12, margin: 0, opacity: .7 }}>
                  {!agent.public_ip ? "No public IP recorded for this agent yet" : `No geo data found for ${agent.public_ip}`}
                </p>
              </div>
            ) : (
              <>
                <div style={{ background: "rgba(var(--vz-primary-rgb),.06)", border: "1px solid rgba(var(--vz-primary-rgb),.15)", borderRadius: 10, padding: "14px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 14 }}>
                  {geo.countryCode && (
                    <img src={`https://flagcdn.com/48x36/${geo.countryCode.toLowerCase()}.png`} alt={geo.country}
                      style={{ borderRadius: 4, border: "1px solid var(--vz-border-color)", flexShrink: 0 }}
                      onError={e => { e.target.style.display = "none"; }} />
                  )}
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--vz-secondary-color)", margin: "0 0 3px" }}>Agent Location</p>
                    <p style={{ fontSize: 15, fontWeight: 700, color: "var(--vz-body-color)", margin: "0 0 2px" }}>
                      {[geo.city, geo.regionName, geo.country].filter(Boolean).join(", ") || "—"}
                    </p>
                    <p style={{ fontSize: 11, color: "var(--vz-secondary-color)", margin: 0, fontFamily: "monospace" }}>{agent.public_ip}</p>
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    {geo.proxy ? (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: "rgba(var(--vz-danger-rgb),.12)", color: "var(--vz-danger)", border: "1px solid rgba(var(--vz-danger-rgb),.3)" }}>
                        <FeatherIcon icon="shield-off" size={11} />VPN/Proxy
                      </span>
                    ) : (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: "rgba(var(--vz-success-rgb),.12)", color: "var(--vz-success)", border: "1px solid rgba(var(--vz-success-rgb),.3)" }}>
                        <FeatherIcon icon="shield" size={11} />Clean
                      </span>
                    )}
                  </div>
                </div>
                <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--vz-secondary-color)", marginBottom: 8 }}>Location</p>
                {[
                  { icon: "globe",      label: "Country",     value: `${geo.country || "—"} ${geo.countryCode ? `(${geo.countryCode})` : ""}` },
                  { icon: "map",        label: "Region",      value: geo.regionName || "—" },
                  { icon: "map-pin",    label: "City",        value: geo.city || "—" },
                  { icon: "clock",      label: "Timezone",    value: geo.timezone || "—" },
                  { icon: "navigation", label: "Coordinates", value: geo.lat && geo.lon ? `${geo.lat}, ${geo.lon}` : "—",
                    link: geo.lat && geo.lon ? `https://www.google.com/maps?q=${geo.lat},${geo.lon}` : null },
                ].map((r, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid var(--vz-border-color)" }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "var(--vz-secondary-color)" }}>
                      <FeatherIcon icon={r.icon} size={12} />{r.label}
                    </span>
                    {r.link ? (
                      <a href={r.link} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: "var(--vz-primary)", fontFamily: "monospace", textDecoration: "none" }}>
                        {r.value} <FeatherIcon icon="external-link" size={10} />
                      </a>
                    ) : (
                      <span style={{ fontSize: 12, fontWeight: 500, color: "var(--vz-body-color)" }}>{r.value}</span>
                    )}
                  </div>
                ))}
                <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--vz-secondary-color)", marginTop: 16, marginBottom: 8 }}>Network</p>
                {[
                  { icon: "wifi",    label: "ISP",         value: geo.isp || "—" },
                  { icon: "server",  label: "Organization", value: geo.org || "—" },
                  { icon: "server",  label: "Hosting / DC", value: geo.hosting ? "Yes — Data Center" : "No", color: geo.hosting ? "var(--vz-warning)" : "var(--vz-success)" },
                  { icon: "eye-off", label: "Proxy / VPN",  value: geo.proxy   ? "Yes — Suspicious"  : "No", color: geo.proxy   ? "var(--vz-danger)"  : "var(--vz-success)" },
                ].map((r, i, arr) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: i < arr.length - 1 ? "1px solid var(--vz-border-color)" : "none" }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "var(--vz-secondary-color)" }}>
                      <FeatherIcon icon={r.icon} size={12} />{r.label}
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 500, color: r.color || "var(--vz-body-color)" }}>{r.value}</span>
                  </div>
                ))}
                {geo.lat && geo.lon && (
                  <div style={{ marginTop: 16, borderRadius: 8, overflow: "hidden", border: "1px solid var(--vz-border-color)" }}>
                    <iframe title="agent-geo" width="100%" height="180" frameBorder="0" style={{ display: "block", border: 0 }}
                      src={`https://www.openstreetmap.org/export/embed.html?bbox=${geo.lon-0.5},${geo.lat-0.5},${geo.lon+0.5},${geo.lat+0.5}&layer=mapnik&marker=${geo.lat},${geo.lon}`}
                      allowFullScreen />
                  </div>
                )}
              </>
            )
          )}

          {/* ── STATUS HISTORY ─────────────────────────── */}
          {activeTab === "history" && (
            evLoading ? (
              <div className="text-center py-5">
                <Spinner size="sm" color="primary" />
                <p style={{ marginTop: 8, fontSize: 13, color: "var(--vz-secondary-color)" }}>Loading history...</p>
              </div>
            ) : events.length === 0 ? (
              <div className="text-center py-5" style={{ color: "var(--vz-secondary-color)" }}>
                <FeatherIcon icon="activity" size={36} className="d-block mx-auto mb-3" style={{ opacity: .3 }} />
                <p style={{ fontSize: 14, fontWeight: 600, margin: "0 0 4px" }}>No Events Yet</p>
                <p style={{ fontSize: 12, margin: 0, opacity: .7 }}>Status changes will appear here once the agent collector detects them</p>
              </div>
            ) : (
              <>
                <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--vz-secondary-color)", marginBottom: 14 }}>
                  Last 30 days · {events.length} event{events.length !== 1 ? "s" : ""}
                </p>
                <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
                  {[
                    { label: "Online",  count: events.filter(e => e.event_type === "online").length,                               color: "var(--vz-success)", bg: "rgba(var(--vz-success-rgb),.12)", border: "rgba(var(--vz-success-rgb),.3)" },
                    { label: "Offline", count: events.filter(e => e.event_type === "offline").length,                              color: "var(--vz-danger)",  bg: "rgba(var(--vz-danger-rgb),.12)",  border: "rgba(var(--vz-danger-rgb),.3)"  },
                    { label: "Changes", count: events.filter(e => e.event_type !== "online" && e.event_type !== "offline").length, color: "var(--vz-warning)", bg: "rgba(var(--vz-warning-rgb),.12)", border: "rgba(var(--vz-warning-rgb),.3)" },
                  ].filter(s => s.count > 0).map((s, i) => (
                    <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 14px", borderRadius: 20, background: s.bg, color: s.color, border: `1px solid ${s.border}`, fontSize: 12, fontWeight: 600 }}>
                      {s.count} × {s.label}
                    </span>
                  ))}
                </div>
                {events.map((ev, i) => {
                  const online   = ev.event_type === "online";
                  const offline  = ev.event_type === "offline";
                  const evColor  = online ? "var(--vz-success)" : offline ? "var(--vz-danger)" : "var(--vz-warning)";
                  const evBg     = online ? "rgba(var(--vz-success-rgb),.12)" : offline ? "rgba(var(--vz-danger-rgb),.12)" : "rgba(var(--vz-warning-rgb),.12)";
                  const evBorder = online ? "var(--vz-success)" : offline ? "var(--vz-danger)" : "var(--vz-warning)";
                  const evIcon   = online ? "arrow-up" : offline ? "arrow-down" : "refresh-cw";
                  const evLabel  = online ? "Came Online" : offline ? "Went Offline" : ev.event_type || "Status Change";
                  return (
                    <div key={i} style={{ display: "flex", gap: 12, paddingBottom: 16, position: "relative" }}>
                      {i < events.length - 1 && (
                        <div style={{ position: "absolute", left: 10, top: 22, bottom: 0, width: 1, background: "var(--vz-border-color)" }} />
                      )}
                      <div style={{ width: 22, height: 22, borderRadius: "50%", flexShrink: 0, background: evBg, border: `2px solid ${evBorder}`, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}>
                        <FeatherIcon icon={evIcon} size={10} style={{ color: evColor }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: evColor }}>{evLabel}</span>
                          <span style={{ fontSize: 11, color: "var(--vz-secondary-color)" }}>{fmtAgo(ev.timestamp)}</span>
                        </div>
                        <div style={{ fontSize: 11, color: "var(--vz-secondary-color)", marginTop: 2 }}>
                          {fmtTs(ev.timestamp)}
                          {ev.agent_ip && ev.agent_ip !== "any" && (
                            <code style={{ marginLeft: 6, fontSize: 10, color: "#0284c7", background: "rgba(2,132,199,.1)", padding: "1px 5px", borderRadius: 3 }}>{ev.agent_ip}</code>
                          )}
                        </div>
                        {ev.from_status && ev.to_status && (
                          <div style={{ fontSize: 11, color: "var(--vz-secondary-color)", marginTop: 3, display: "flex", alignItems: "center", gap: 4, opacity: .7 }}>
                            <span style={{ padding: "1px 6px", background: "var(--vz-light, rgba(0,0,0,.04))", borderRadius: 4 }}>{ev.from_status}</span>
                            <FeatherIcon icon="arrow-right" size={10} />
                            <span style={{ padding: "1px 6px", background: "var(--vz-light, rgba(0,0,0,.04))", borderRadius: 4 }}>{ev.to_status}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </>
            )
          )}

          {/* ── LOGIN HISTORY ─────────────────────────── */}
          {activeTab === "login" && (
            loginLoading ? (
              <div className="text-center py-5">
                <Spinner size="sm" color="primary" />
                <p style={{ marginTop: 8, fontSize: 13, color: "var(--vz-secondary-color)" }}>Loading login history...</p>
              </div>
            ) : loginHistory.length === 0 ? (
              <div className="text-center py-5" style={{ color: "var(--vz-secondary-color)" }}>
                <FeatherIcon icon="log-in" size={36} className="d-block mx-auto mb-3" style={{ opacity: .3 }} />
                <p style={{ fontSize: 14, fontWeight: 600, margin: "0 0 4px" }}>No Login Events Yet</p>
                <p style={{ fontSize: 12, margin: 0, opacity: .7 }}>Machine and user session events will appear here</p>
              </div>
            ) : (
              <>
                {/* User Session summary card */}
                <div style={{ background: "rgba(2,132,199,.06)", border: "1px solid rgba(2,132,199,.2)", borderRadius: 10, padding: "14px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 20 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(2,132,199,.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <FeatherIcon icon="user" size={18} style={{ color: "#0284c7" }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "#0284c7", margin: "0 0 6px" }}>User Sessions — Last 30 Days</p>
                    <div style={{ display: "flex", gap: 20 }}>
                      <div>
                        <p style={{ fontSize: 22, fontWeight: 800, color: "#0284c7", margin: 0, lineHeight: 1 }}>{userEvents.filter(e => e.event_type === "user_login").length}</p>
                        <p style={{ fontSize: 11, color: "var(--vz-secondary-color)", margin: "2px 0 0" }}>Logins</p>
                      </div>
                      <div style={{ width: 1, background: "rgba(2,132,199,.2)" }} />
                      <div>
                        <p style={{ fontSize: 22, fontWeight: 800, color: "#7c3aed", margin: 0, lineHeight: 1 }}>{userEvents.filter(e => e.event_type === "user_logout").length}</p>
                        <p style={{ fontSize: 11, color: "var(--vz-secondary-color)", margin: "2px 0 0" }}>Logouts</p>
                      </div>
                      <div style={{ width: 1, background: "rgba(2,132,199,.2)" }} />
                      <div>
                        <p style={{ fontSize: 22, fontWeight: 800, color: "var(--vz-body-color)", margin: 0, lineHeight: 1 }}>{userEvents.length}</p>
                        <p style={{ fontSize: 11, color: "var(--vz-secondary-color)", margin: "2px 0 0" }}>Total</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Timeline */}
                <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--vz-secondary-color)", marginBottom: 14 }}>
                  Last 30 days · {userEvents.length} event{userEvents.length !== 1 ? "s" : ""}
                </p>

                {userEvents.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "24px 0", color: "var(--vz-secondary-color)" }}>
                    <FeatherIcon icon="user" size={28} style={{ opacity: .3, display: "block", margin: "0 auto 8px" }} />
                    <p style={{ fontSize: 13 }}>No user session events in the last 30 days</p>
                  </div>
                ) : (
                  userEvents.map((ev, i) => {
                    const meta = loginEventMeta(ev);
                    return (
                      <div key={i} style={{ display: "flex", gap: 12, paddingBottom: 14, position: "relative" }}>
                        {i < userEvents.length - 1 && (
                          <div style={{ position: "absolute", left: 10, top: 22, bottom: 0, width: 1, background: "var(--vz-border-color)", opacity: .5 }} />
                        )}
                        {/* Icon */}
                        <div style={{ width: 22, height: 22, borderRadius: "50%", flexShrink: 0, background: meta.bg, border: `2px solid ${meta.border}`, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}>
                          <FeatherIcon icon={meta.icon} size={10} style={{ color: meta.color }} />
                        </div>

                        {/* Content */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 6 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <span style={{ fontSize: 13, fontWeight: 600, color: meta.color }}>{meta.label}</span>
                            </div>
                            <span style={{ fontSize: 11, color: "var(--vz-secondary-color)", flexShrink: 0 }}>{fmtAgo(ev.timestamp)}</span>
                          </div>

                          <div style={{ fontSize: 11, color: "var(--vz-secondary-color)", marginTop: 2 }}>
                            {fmtTs(ev.timestamp)}
                          </div>

                          {/* Status transition */}
                          {ev.from_status && ev.to_status && (
                            <div style={{ fontSize: 11, marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
                              <span style={{ padding: "1px 7px", borderRadius: 4, background: "var(--vz-light, rgba(0,0,0,.04))", color: "var(--vz-secondary-color)", fontFamily: "monospace", fontSize: 10 }}>
                                {ev.from_status}
                              </span>
                              <FeatherIcon icon="arrow-right" size={10} style={{ color: "var(--vz-secondary-color)", opacity: .5 }} />
                              <span style={{ padding: "1px 7px", borderRadius: 4, background: meta.bg, color: meta.color, fontFamily: "monospace", fontSize: 10, fontWeight: 600 }}>
                                {ev.to_status}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}

                <div style={{ marginTop: 8, fontSize: 11, color: "var(--vz-secondary-color)", textAlign: "right", opacity: .6 }}>
                  Showing last 30 days
                </div>
              </>
            )
          )}

        </div>
      </div>
    </>
  );
};

/* ─── Main AssetInventoryTable ───────────────────────────────────── */
const AssetInventoryTable = () => {
  const [agents,   setAgents]   = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [page,     setPage]     = useState(1);
  const [search,   setSearch]   = useState("");
  const [status,   setStatus]   = useState("all");
  const [summary,  setSummary]  = useState({ total: 0, online: 0, offline: 0, never: 0 });
  const [selected, setSelected] = useState(null);

  const creds = () => ({
    apiUrl: localStorage.getItem("flaskApiUrl"),
    apiKey: localStorage.getItem("flaskApiKey"),
  });

  const authFetch = useCallback(async (url) => {
    const { apiUrl, apiKey } = creds();
    if (!apiUrl || !apiKey) throw new Error("Credentials not configured");
    const r = await fetch(`${apiUrl}${url}`, {
      headers: { "Content-Type": "application/json", "X-API-Key": apiKey },
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.error || "Request failed");
    return d;
  }, []); // eslint-disable-line

  const fetchAgents = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await authFetch("/api/wazuh/agents");
      if (data?.success) {
        const list    = (data.agents || []).filter(a => a.id !== "000" && !HIDDEN_AGENT_IDS.includes(a.id) && (a.status || "").toLowerCase() !== "unknown");
        const online  = list.filter(a => ["active","online"].some(s => (a.status||"").toLowerCase().includes(s))).length;
        const offline = list.filter(a => ["disconnect","offline"].some(s => (a.status||"").toLowerCase().includes(s))).length;
        const never   = list.filter(a => (a.status||"").toLowerCase().includes("never")).length;
        setAgents(list);
        setSummary({ total: list.length, online, offline, never });
      }
    } catch (e) { setError(e.message); }
    finally     { setLoading(false); }
  }, [authFetch]);

  useEffect(() => {
    fetchAgents();
    const t = setInterval(fetchAgents, 30000);
    return () => clearInterval(t);
  }, [fetchAgents]);

  useEffect(() => {
    let f = agents;
    if (status !== "all") {
      f = f.filter(a => {
        const s = (a.status || "").toLowerCase();
        if (status === "online")  return s.includes("active") || s.includes("online");
        if (status === "offline") return s.includes("disconnect") || s.includes("offline");
        if (status === "never")   return s.includes("never");
        return true;
      });
    }
    if (search) {
      const t = search.toLowerCase();
      f = f.filter(a =>
        (a.name||"").toLowerCase().includes(t) ||
        (a.id||"").includes(t)                 ||
        (a.ip||"").includes(t)                 ||
        (a.os||"").toLowerCase().includes(t)   ||
        (a.public_ip||"").includes(t)
      );
    }
    setFiltered(f);
    setPage(1);
  }, [search, status, agents]);

  const totalPages  = Math.ceil(filtered.length / ROWS);
  const currentRows = filtered.slice((page - 1) * ROWS, page * ROWS);

  const filterBtn = (val, label, count, color) => {
    const active = status === val;
    return (
      <button key={val} onClick={() => setStatus(val)} style={{
        padding: "5px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
        background: active ? (color || "var(--vz-primary)") : "transparent",
        color:      active ? "#fff" : "var(--vz-secondary-color)",
        border:     active ? `1px solid ${color || "var(--vz-primary)"}` : "1px solid var(--vz-border-color)",
        transition: "all .15s",
      }}>
        {label} <span style={{ opacity: .8 }}>{count}</span>
      </button>
    );
  };

  const IpCell = ({ agent }) => {
    const ip = agent.public_ip || (agent.ip !== "any" ? agent.ip : null);
    if (ip) return <span style={{ fontFamily: "monospace", fontSize: 13, color: "var(--vz-body-color)" }}>{ip}</span>;
    return <span style={{ fontSize: 11, opacity: .45 }}>—</span>;
  };

  return (
    <>
      <style>{`
        @keyframes spin    { to { transform: rotate(360deg); } }
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
      `}</style>

      {error && (
        <div style={{ padding: "10px 16px", background: "rgba(var(--vz-danger-rgb),.1)", border: "1px solid rgba(var(--vz-danger-rgb),.3)", borderRadius: 8, fontSize: 13, color: "var(--vz-danger)", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
          <FeatherIcon icon="alert-circle" size={14} />{error}
        </div>
      )}

      {/* Filter bar */}
      <div style={{ ...card, marginBottom: 12, padding: "12px 18px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "space-between" }}>
          <div style={{ position: "relative", flex: "1 1 200px", maxWidth: 320 }}>
            <FeatherIcon icon="search" size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--vz-secondary-color)", pointerEvents: "none" }} />
            <input type="text" placeholder="Search by name, ID, IP or OS..."
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ width: "100%", paddingLeft: 30, paddingRight: search ? 30 : 12, height: 34, border: "1px solid var(--vz-border-color)", borderRadius: 8, fontSize: 13, color: "var(--vz-body-color)", background: "var(--vz-card-bg, #fff)", outline: "none" }}
            />
            {search && (
              <button onClick={() => setSearch("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--vz-secondary-color)", padding: 0 }}>✕</button>
            )}
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {filterBtn("all",     "All",     summary.total)}
            {filterBtn("online",  "Online",  summary.online,  "#16a34a")}
            {filterBtn("offline", "Offline", summary.offline, "#dc2626")}
            {filterBtn("never",   "Never",   summary.never,   "#d97706")}
          </div>
        </div>
      </div>

      {/* Table */}
      <div style={card}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--vz-border-color)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: "var(--vz-body-color)" }}>Agent Inventory</span>
            <span style={{ fontSize: 12, padding: "2px 10px", background: "rgba(var(--vz-primary-rgb),.1)", borderRadius: 20, color: "var(--vz-primary)", fontWeight: 600 }}>
              {filtered.length}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 11, color: "var(--vz-secondary-color)" }}>
              <FeatherIcon icon="mouse-pointer" size={11} className="me-1" />Click a row for details
            </span>
            <button onClick={fetchAgents} disabled={loading}
              style={{ height: 32, padding: "0 12px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", background: "transparent", color: "var(--vz-primary)", border: "1px solid var(--vz-primary)", display: "flex", alignItems: "center", gap: 4 }}>
              <FeatherIcon icon="refresh-cw" size={13} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
              Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: "48px 24px", textAlign: "center" }}>
            <Spinner color="primary" />
            <p style={{ marginTop: 8, color: "var(--vz-secondary-color)", fontSize: 13 }}>Loading agents...</p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["Agent Name", "Agent ID", "IP Address", "Operating System", "Status", ""].map((h, i) => (
                      <th key={i} style={{ padding: "10px 18px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--vz-secondary-color)", textAlign: "left", borderBottom: "1px solid var(--vz-border-color)", background: "var(--vz-light, rgba(0,0,0,.02))", whiteSpace: "nowrap" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentRows.length === 0 ? (
                    <tr>
                      <td colSpan={6} style={{ padding: "48px 24px", textAlign: "center", color: "var(--vz-secondary-color)" }}>
                        <FeatherIcon icon="inbox" size={36} className="d-block mx-auto mb-2" />
                        {search || status !== "all" ? "No agents match your filters" : "No agents found"}
                        {(search || status !== "all") && (
                          <button onClick={() => { setSearch(""); setStatus("all"); }}
                            style={{ display: "block", margin: "10px auto 0", background: "none", border: "none", color: "var(--vz-primary)", fontWeight: 600, cursor: "pointer", textDecoration: "underline", fontSize: 13 }}>
                            Clear filters
                          </button>
                        )}
                      </td>
                    </tr>
                  ) : currentRows.map((agent) => {
                    const sm         = statusMeta(agent.status);
                    const isSelected = selected?.id === agent.id;
                    return (
                      <tr key={agent.id}
                        onClick={() => setSelected(isSelected ? null : agent)}
                        style={{ borderBottom: "1px solid var(--vz-border-color)", cursor: "pointer", transition: "background .15s", background: isSelected ? "rgba(var(--vz-primary-rgb),.06)" : "" }}
                        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = "rgba(var(--vz-primary-rgb),.03)"; }}
                        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = ""; }}
                      >
                        <td style={{ padding: "12px 18px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ width: 32, height: 32, borderRadius: "50%", background: sm.bg, border: `1px solid ${sm.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                              <FeatherIcon icon="monitor" size={14} style={{ color: sm.color }} />
                            </div>
                            <div>
                              <span style={{ fontWeight: 600, fontSize: 13, color: "var(--vz-body-color)", display: "block" }}>{agent.name || "Unknown"}</span>
                              <span style={{ fontSize: 10, color: "var(--vz-secondary-color)" }}>{fmtAgo(agent.last_updated)}</span>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: "12px 18px" }}>
                          <code style={{ fontSize: 12, color: "var(--vz-primary)", background: "rgba(var(--vz-primary-rgb),.1)", padding: "2px 8px", borderRadius: 4 }}>{agent.id}</code>
                        </td>
                        <td style={{ padding: "12px 18px" }}><IpCell agent={agent} /></td>
                        <td style={{ padding: "12px 18px", fontSize: 13, color: "var(--vz-secondary-color)" }}>
                          {agent.os ? agent.os.replace("Microsoft ", "") : "Unknown"}
                        </td>
                        <td style={{ padding: "12px 18px" }}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: sm.bg, color: sm.color, border: `1px solid ${sm.border}` }}>
                            <span style={{ width: 6, height: 6, borderRadius: "50%", background: sm.dot }} />
                            {sm.text}
                          </span>
                        </td>
                        <td style={{ padding: "12px 18px" }}>
                          <FeatherIcon icon="chevron-right" size={15} style={{ color: isSelected ? "var(--vz-primary)" : "var(--vz-secondary-color)", transition: "transform .2s", transform: isSelected ? "rotate(90deg)" : "none" }} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div style={{ padding: "12px 18px", borderTop: "1px solid var(--vz-border-color)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", gap: 6 }}>
                  {[["← Prev", -1], ["Next →", 1]].map(([label, dir]) => {
                    const disabled = dir === -1 ? page === 1 : page === totalPages;
                    return (
                      <button key={label} onClick={() => setPage(p => p + dir)} disabled={disabled}
                        style={{ height: 32, padding: "0 12px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer", background: "transparent", color: disabled ? "var(--vz-secondary-color)" : "var(--vz-primary)", border: `1px solid ${disabled ? "var(--vz-border-color)" : "var(--vz-primary)"}`, opacity: disabled ? .5 : 1 }}>
                        {label}
                      </button>
                    );
                  })}
                </div>
                <span style={{ fontSize: 12, color: "var(--vz-secondary-color)" }}>Page {page} of {totalPages}</span>
              </div>
            )}

            {filtered.length > 0 && (
              <div style={{ padding: "10px 18px", borderTop: "1px solid var(--vz-border-color)", background: "rgba(var(--vz-primary-rgb),.02)", display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 12, color: "var(--vz-secondary-color)" }}>
                  Showing <strong style={{ color: "var(--vz-primary)" }}>{filtered.length}</strong> of <strong style={{ color: "var(--vz-primary)" }}>{agents.length}</strong> agents
                </span>
                <span style={{ fontSize: 12, color: "var(--vz-secondary-color)" }}>
                  <FeatherIcon icon="clock" size={11} className="me-1" />Auto-refreshes every 30s
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {selected && (
        <AgentDetailPanel
          agent={selected}
          onClose={() => setSelected(null)}
          authFetch={authFetch}
        />
      )}
    </>
  );
};

export default AssetInventoryTable;